import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import ArrowForward from "@store/Components/Icons/ArrowForward";
import { useMergeProps } from "@store/Hooks/Common";
import { TYPOGRAPHY } from "@store/theme/design";
import { imageStyles, scalableIconStyles, transitionAnimationStyles } from "@store/theme/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { DetailedReactHTMLElement } from "react";
import { css } from "styled-components";

const StyledSpan = getStyledWrapper("span");

export interface StoreCallToActionCardProps {
    link?: LinkProps;
    title?: string;
    content?: string | DetailedReactHTMLElement<{}, HTMLElement> | DetailedReactHTMLElement<{}, HTMLElement>[];
    image?: LazyImageProps;
    imageRatio?: number;
    lineEllipsis?: number;
    extendedStyles?: StoreCallToActionCardStyles;
}

export interface StoreCallToActionCardStyles {
    link?: LinkPresentationProps;
    linkHoverCss?: StyledProp;
    imageWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    descriptionWrapper?: InjectableCss;
    description?: TypographyPresentationProps;
    icon?: IconProps;
    image?: LazyImageProps;
}

export const storeCallToActionCardStyles: StoreCallToActionCardStyles = {
    link: {
        css: css`
            display: block;
            width: 100%;
        `,
    },
    linkHoverCss: css`
        ${IconWrapper} {
            transform: translateX(25%);
        }

        img {
            transform: scale(1.1);
        }
    `,
    imageWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
        `,
    },
    image: {
        css: css`
            ${props => props.theme.lazyImage?.defaultProps?.css}

            img {
                transition: ${transitionAnimationStyles("transform")};
            }
        `,
    },
    contentWrapper: {
        css: css`
            position: relative;
            display: block;
            background-color: ${getColor("common.background")};
            font-weight: ${TYPOGRAPHY.weight.light};
            line-height: ${TYPOGRAPHY.lineHeight.small};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["20px 15px", null, null, "25px 20px"], properties: ["padding"] },
                        { values: ["-60px", null, null, "-86px"], properties: ["margin-top"] },
                        { values: [null, null, null, "108px"], properties: ["min-height"] },
                        { values: ["91%", null, null, "77%"], properties: ["width"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    title: {
        variant: "h3",
        forwardAs: "span",
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.25em;
            word-wrap: break-word;
        `,
    },
    descriptionWrapper: {
        css: css`
            display: block;
            padding-right: 10%;
        `,
    },
    icon: {
        src: ArrowForward,
        color: "primary",
        css: css`
            ${scalableIconStyles()}
            margin-left: 0.5em;
            transition: ${transitionAnimationStyles("transform")};

            svg {
                max-width: 24px;
            }
        `,
    },
};

const StoreCallToActionCard: React.FC<StoreCallToActionCardProps> = ({
    link,
    title,
    content,
    image,
    imageRatio,
    extendedStyles,
}) => {
    const styles = useMergeProps(storeCallToActionCardStyles, extendedStyles);
    const hasLink = link?.href || link?.onClick;

    const linkStyles = css`
        ${styles.link?.css}
        ${hasLink
            ? css`
                  &:hover {
                      ${styles.linkHoverCss}
                  }
              `
            : css`
                  cursor: default;
              `}
    `;

    const imageWrapperStyles = css`
        ${imageStyles(imageRatio)}
        ${styles.imageWrapper?.css}
    `;

    return (
        <Link {...link} css={linkStyles}>
            <StyledSpan {...styles.imageWrapper} css={imageWrapperStyles}>
                {image?.src ? <LazyImage {...image} {...styles.image}></LazyImage> : null}
            </StyledSpan>
            <StyledSpan {...styles.contentWrapper}>
                <Typography {...styles.title}>
                    <StyledSpan>{title}</StyledSpan>
                    {hasLink ? <Icon {...styles.icon} /> : null}
                </Typography>
                {content ? (
                    <StyledSpan {...styles.descriptionWrapper}>
                        <Typography {...styles.description}>{content}</Typography>
                    </StyledSpan>
                ) : null}
            </StyledSpan>
        </Link>
    );
};

export default StoreCallToActionCard;
